import { Component, useState, useEffect, useCallback, useRef } from 'react';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { F, R, S, T, tone } from './theme';
import { DATE_PRESETS, PHOTO_ANGLES, expiryInfo, toDateSafe } from './lib/format';

export function Logo({ size = 36, radius = 10 }) {
  return (
    <img
      src="/saikapp-icon.png"
      alt="SaikApp"
      width={size}
      height={size}
      style={{ borderRadius: radius, display: 'block', flexShrink: 0 }}
    />
  );
}

// ── Responsive hook ──────────────────────────────────────

export function useIsMobile(breakpoint = 820) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// ── Inline SVG icons (no dependencies) ───────────────────

export function Icon({ name, size = 22, color = 'currentColor', strokeWidth = 2 }) {
  const paths = {
    dashboard: (<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></>),
    users: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
    bike: (<><circle cx="18.5" cy="17.5" r="3.5" /><circle cx="5.5" cy="17.5" r="3.5" /><circle cx="15" cy="5" r="1" /><path d="M12 17.5V14l-3-3 4-3 2 3h2" /></>),
    calendar: (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
    alert: (<><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>),
    clock: (<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>),
    check: (<polyline points="20 6 9 17 4 12" />),
    x: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
    logout: (<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>),
    external: (<><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></>),
    chevronRight: (<polyline points="9 18 15 12 9 6" />),
    chevronLeft: (<polyline points="15 18 9 12 15 6" />),
    pin: (<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" /><circle cx="12" cy="10" r="3" /></>),
    image: (<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>),
    money: (<><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>),
    search: (<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
    shield: (<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />),
    download: (<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>),
    megaphone: (<><path d="M3 11l18-5v12L3 13v-2z" /><path d="M11.6 16.8a2 2 0 0 1-3.2 2.4L6 15" /></>),
    sun: (<><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" /></>),
    moon: (<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />),
    edit: (<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>),
    star: (<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />),
    settings: (<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" /></>),
    'arrow-down': (<><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>),
    menu: (<><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>),
    chevronDown: (<polyline points="6 9 12 15 18 9" />),
    chevronUp: (<polyline points="18 15 12 9 6 15" />),
    sliders: (<><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></>),
    'arrow-up': (<><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

// ── Shared Components ────────────────────────────────────

export function Badge({ label, color, bg, border }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 6,
      fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
      letterSpacing: 0.1, textTransform: 'capitalize',
      color, backgroundColor: bg, border: `1px solid ${border}`,
    }}>{label}</span>
  );
}

export function DocLink({ label, url, expiryMonth, expiryYear }) {
  if (!url) {
    return (
      <span style={{ color: T.danger, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Icon name="x" size={12} color={T.danger} /> {label} missing
      </span>
    );
  }

  const info = expiryInfo(expiryMonth, expiryYear);

  return (
    <a href={url} target="_blank" rel="noreferrer" style={{
      color: T.primary, fontSize: 12, textDecoration: 'none',
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      <Icon name="image" size={12} color={T.primary} /> {label}
      {info && <span style={{ color: info.color, fontWeight: 700 }}>{` · Exp ${info.label}`}</span>}
    </a>
  );
}

export function ActionBtn({ label, icon, onClick, variant = 'approve', full = false, disabled = false }) {
  const st = tone({ approve: 'success', reject: 'danger', warning: 'warning' }[variant] || 'success');
  return (
    <button onClick={onClick} disabled={disabled} className="ui-btn" style={{
      backgroundColor: st.bg, border: `1px solid ${st.border}`,
      borderRadius: 9, padding: '8px 13px',
      color: st.color, cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 12.5, fontWeight: 600, opacity: disabled ? 0.45 : 1,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      flex: full ? 1 : 'none', whiteSpace: 'nowrap',
    }}>
      {icon && <Icon name={icon} size={14} color={st.color} />}
      {label}
    </button>
  );
}

/**
 * A secondary action.
 *
 * Approve, Reject and Revoke decide something about a person; Edit name and
 * Replace document are filing. They used to be the same button in different
 * colours, all the same size, which made the row read as six equal choices.
 */
export function ToolBtn({ label, icon, onClick, highlight = false }) {
  return (
    <button onClick={onClick} className="ui-btn" style={{
      backgroundColor: 'transparent',
      border: `1px solid ${highlight ? T.primaryBorder : T.cardBorder}`,
      borderRadius: 8, padding: '6px 10px',
      color: highlight ? T.primary : T.textMuted,
      cursor: 'pointer', fontSize: 12, fontWeight: 600,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
      fontFamily: 'inherit', whiteSpace: 'nowrap',
    }}>
      {icon && <Icon name={icon} size={12} color={highlight ? T.primary : T.textMuted} />}
      {label}
    </button>
  );
}

export function Avatar({ name, color = T.primary, bg = T.primaryDim, size = 40 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      backgroundColor: bg, border: `2px solid ${color}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color, fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
    }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

// Shows the user's actual profile photo if set; falls back to initials avatar.
// The lock icon overlay signals the photo is locked (can't be changed by user).

export function UserAvatar({ user, color, bg, size = 44 }) {
  if (user.photoURL) {
    return (
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <img
          src={user.photoURL}
          alt={user.name}
          style={{
            width: size, height: size, borderRadius: size / 2,
            objectFit: 'cover', border: `2px solid ${color}`,
            display: 'block',
          }}
        />
        {user.photoLocked && (() => {
          // Scales with the portrait: a fixed 16px badge is a speck at 96.
          const b = Math.max(16, Math.round(size * 0.24));
          return (
            <div style={{
              position: 'absolute', bottom: 0, right: 0,
              width: b, height: b, borderRadius: b / 2,
              backgroundColor: T.success, border: `2px solid ${T.card}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name="shield" size={Math.round(b * 0.55)} color="#fff" strokeWidth={2.5} />
            </div>
          );
        })()}
      </div>
    );
  }
  return <Avatar name={user.name} color={color} bg={bg} size={size} />;
}

// ── Profile viewer ────────────────────────────────────────
// Every tab shows people, and until now each one was a dead end: a name in a
// booking row told you nothing about who that person is. Rather than thread an
// opener through eleven components, the whole app is wrapped in this context so
// any name anywhere can become a link.

/**
 * The one card.
 *
 * There were four copies of this div — Analytics kept a private `Card`, and
 * Users, Bikes and Bookings each inlined the same thing with slightly
 * different padding and a slightly different title size. Nothing kept them in
 * step, so they drifted, and a page built out of them read as a pile rather
 * than a list.
 *
 * FLAT BY DEFAULT, AND THAT IS THE POINT. Every card used to carry T.shadow.
 * Nineteen of them down one page is nineteen grey halos on a near-white
 * background, and when everything is raised nothing is. The shadow is kept for
 * things that genuinely float — modals, the save bar, the nav sheet.
 *
 * `toneName` is the only way to make a card louder, and it should mean
 * something is wrong or waiting, not that the card mattered to whoever wrote
 * it.
 */
export function Card({
  title, sub, right, icon, toneName, children,
  stack = false, flush = false, pad, gap = S.md, last = false, id,
}) {
  const tn = toneName ? tone(toneName) : null;
  const hasHead = title != null || right != null;
  return (
    <div id={id} style={{
      backgroundColor: T.card,
      border: `1px solid ${tn ? tn.border : T.cardBorder}`,
      borderRadius: R.lg,
      padding: flush ? 0 : (pad != null ? pad : S.lg),
      marginBottom: last ? 0 : 16,
      overflow: flush ? 'hidden' : undefined,
    }}>
      {hasHead && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: S.md,
          marginBottom: flush ? 0 : (children ? S.md : 0),
          padding: flush ? `${S.md}px ${S.lg}px` : 0,
          borderBottom: flush ? `1px solid ${T.cardBorder}` : undefined,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: S.sm, minWidth: 0, alignItems: 'flex-start', flex: '1 1 auto' }}>
            {icon && (
              <div style={{
                width: 28, height: 28, borderRadius: R.sm, flexShrink: 0,
                backgroundColor: tn ? tn.bg : T.cardInner,
                border: `1px solid ${tn ? tn.border : T.cardBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={icon} size={14} color={tn ? tn.color : T.textMuted} />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ color: T.textPrimary, fontWeight: 700, fontSize: F.lead, letterSpacing: -0.2 }}>{title}</div>
              {sub && (
                <div style={{ color: T.textMuted, fontSize: F.small, marginTop: 3, lineHeight: 1.55 }}>{sub}</div>
              )}
            </div>
          </div>
          {right != null && <div style={{ flexShrink: 0 }}>{right}</div>}
        </div>
      )}
      {stack
        ? <div style={{ display: 'flex', flexDirection: 'column', gap }}>{children}</div>
        : children}
    </div>
  );
}

export function StatCard({ label, value, toneName = 'primary', sub, icon }) {
  const tn = tone(toneName);
  return (
    <div style={{
      backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
      borderRadius: R.lg, padding: S.md,
      display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: S.sm }}>
        <div style={{ color: T.textPrimary, fontSize: F.figure, fontWeight: 700, lineHeight: 1.1, letterSpacing: -0.8 }}>{value}</div>
        <div style={{
          width: 28, height: 28, borderRadius: R.sm,
          backgroundColor: tn.bg, border: `1px solid ${tn.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={icon} size={14} color={tn.color} />
        </div>
      </div>
      <div style={{ color: T.textSecondary, fontSize: F.small, fontWeight: 600, marginTop: S.xs }}>{label}</div>
      {sub && <div style={{ color: T.textMuted, fontSize: F.micro, lineHeight: 1.5 }}>{sub}</div>}
    </div>
  );
}

/**
 * Several numbers that belong together, in one card rather than one card each.
 *
 * Context figures — sign-ups today, rentals running — do not each deserve the
 * weight of a StatCard, and giving them one is how a dashboard ends up with
 * eight boxes of which three matter.
 */
export function StatStrip({ items }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap',
      backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
      borderRadius: R.lg, marginBottom: 16, overflow: 'hidden',
    }}>
      {items.map((it, i) => (
        // Equal columns that never wrap on a phone: letting them wrap left the
        // third one on a row of its own with no divider beside it, reading as
        // a separate thing rather than the third of three.
        <div key={it.label} style={{
          flex: isMobile ? '1 1 0' : '1 1 140px',
          padding: isMobile ? `${S.sm}px ${S.sm}px ${S.md}px` : `${S.md}px ${S.lg}px`,
          borderLeft: i === 0 ? 'none' : `1px solid ${T.divider}`,
          minWidth: 0,
        }}>
          <div style={{
            color: it.toneName ? tone(it.toneName).color : T.textPrimary,
            fontSize: isMobile ? F.title : F.display,
            fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.15,
          }}>{it.value}</div>
          <div style={{
            color: T.textMuted, fontSize: isMobile ? F.micro : F.small,
            marginTop: 2, lineHeight: 1.35,
          }}>{it.label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * The page title.
 *
 * ON A PHONE THIS DOES NOT PRINT THE TITLE. The sticky top bar is already
 * showing the page name as the heading, and it is still showing it when this
 * has scrolled away, so printing it again here was 22px of type plus a 22px
 * margin — a third of the space above the fold — spent saying the same word
 * twice. The subtitle stays, because that is the part the bar has no room
 * for and the part that says what the page is actually for.
 */
export function SectionHeader({ title, subtitle }) {
  const isMobile = useIsMobile();
  if (isMobile) {
    if (!subtitle) return null;
    return (
      <p style={{
        color: T.textMuted, fontSize: F.small, margin: '0 0 14px', lineHeight: 1.5,
      }}>{subtitle}</p>
    );
  }
  return (
    <div style={{ marginBottom: 22 }}>
      <h1 style={{
        color: T.textPrimary, fontSize: F.display, fontWeight: 700,
        margin: 0, letterSpacing: -0.6,
      }}>{title}</h1>
      {subtitle && (
        <p style={{ color: T.textMuted, fontSize: F.body, margin: '5px 0 0', lineHeight: 1.5 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// Segmented control — one connected pill rather than separate buttons, so the
// filter row reads as a single unit.
//
// ON A PHONE IT SCROLLS SIDEWAYS INSTEAD OF WRAPPING. Bikes has nine options
// and Users has two of these bars; wrapping turned each into two or three
// stacked rows, which is most of why the first actual row of data sat 600px
// down the page. One row that scrolls is shorter, and it says plainly that
// there is more to the right.
//
// `sticky` keeps it under the top bar while a long list scrolls past, so the
// filter you are looking at stays reachable — changing tab used to mean
// scrolling back over a few hundred accounts to reach it.

export function FilterBar({ options, value, onChange, counts = {}, labels, bare = false }) {
  const isMobile = useIsMobile();
  const barRef = useRef(null);

  /**
   * Keep the chosen option on screen.
   *
   * On a phone this row scrolls sideways under a fade, so the last option in
   * a long set — the fourth section tab, the ninth bike filter — could be
   * picked and then sit half off the right-hand edge, greyed out by the mask.
   * The scroll position is set directly rather than through scrollIntoView,
   * which would also scroll the page.
   */
  useEffect(() => {
    const bar = barRef.current;
    if (!isMobile || !bar) return;
    const el = bar.querySelector('[data-active="true"]');
    if (!el) return;
    const left = el.offsetLeft;
    const right = left + el.offsetWidth;
    // 22px is the width of the fade at the right edge, from .ui-fade-x.
    if (left < bar.scrollLeft) bar.scrollLeft = Math.max(0, left - 12);
    else if (right > bar.scrollLeft + bar.clientWidth - 22) {
      bar.scrollLeft = right - bar.clientWidth + 22;
    }
  }, [value, isMobile]);

  return (
    <div
      ref={barRef}
      className={isMobile ? 'ui-scroll-x ui-fade-x' : undefined}
      style={{
        display: isMobile ? 'flex' : 'inline-flex',
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : 'visible',
        maxWidth: '100%',
        // So it shrinks and scrolls rather than shoving a button off the row
        // when it shares a StickyBar with one.
        flex: isMobile ? '1 1 0' : undefined,
        minWidth: 0,
        marginBottom: bare ? 0 : 18,
        backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
        borderRadius: R.md, padding: 3, gap: 2,
      }}
    >
      {options.map(opt => {
        const active = value === opt;
        return (
          <button key={opt} onClick={() => onChange(opt)} className="ui-btn" data-active={active} style={{
            backgroundColor: active ? T.card : 'transparent',
            border: active ? `1px solid ${T.cardBorder}` : '1px solid transparent',
            boxShadow: active ? T.shadow : 'none',
            borderRadius: R.sm, padding: '6px 12px',
            color: active ? T.textPrimary : T.textMuted,
            cursor: 'pointer', fontSize: F.small, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {labels?.[opt] ?? (opt.charAt(0).toUpperCase() + opt.slice(1))}
            {counts[opt] !== undefined && (
              <span style={{
                backgroundColor: active ? T.primaryDim : 'transparent',
                color: active ? T.primary : T.textMuted,
                borderRadius: 5, padding: '0 5px', fontSize: F.micro, fontWeight: 700,
                minWidth: 16, textAlign: 'center',
              }}>{counts[opt] ?? 0}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The row a list is steered from: its main filter, and whatever acts on the
 * selection.
 *
 * ON A PHONE IT STAYS PUT while the list scrolls under it. Changing from
 * Pending to All used to mean scrolling back over however many hundred rows
 * you had got through, which is why the filter mostly did not get changed.
 *
 * It bleeds through the shell's 16px padding deliberately: without that the
 * list shows through two transparent gutters as it passes underneath, which
 * looks like a rendering fault.
 *
 * On a desktop it is the row it always was — filter at one end, actions at
 * the other.
 */
export function StickyBar({ children }) {
  const isMobile = useIsMobile();
  if (!isMobile) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 12,
      }}>{children}</div>
    );
  }
  return (
    <div style={{
      position: 'sticky', top: 'var(--topbar-h)', zIndex: 40,
      backgroundColor: T.bg, margin: '0 -16px 12px', padding: '8px 16px',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>{children}</div>
  );
}

/**
 * The filters that are not the main one.
 *
 * A date range is four preset pills, two date inputs and a Clear; a sort
 * toggle is two more pills and a label. On a phone that is five stacked rows
 * of controls sitting above the list permanently, for something an admin
 * changes about once a week. Behind one tap instead, with a count so it is
 * obvious when something is still filtered.
 *
 * On a desktop there is room, so they sit in a row and nothing is hidden: the
 * component does nothing there beyond the layout.
 */
export function Filters({ children, count = 0, label = 'Filters' }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  if (!isMobile) {
    return (
      <div className="ui-filters" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        gap: S.lg, marginBottom: 16,
      }}>{children}</div>
    );
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <button onClick={() => setOpen(o => !o)} className="ui-btn" style={{
        backgroundColor: count ? T.primaryDim : T.card,
        border: `1px solid ${count ? T.primaryBorder : T.cardBorder}`,
        borderRadius: R.md, padding: '9px 12px', width: '100%',
        color: count ? T.primary : T.textSecondary,
        cursor: 'pointer', fontSize: F.body, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Icon name="sliders" size={15} color={count ? T.primary : T.textMuted} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          {label}{count ? ` · ${count} on` : ''}
        </span>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={15} color={count ? T.primary : T.textMuted} />
      </button>
      {open && (
        <div className="ui-filters" style={{
          marginTop: S.sm, padding: S.md,
          backgroundColor: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: R.md,
          display: 'flex', flexDirection: 'column', gap: S.md,
        }}>{children}</div>
      )}
    </div>
  );
}

/**
 * Firestore date fields in this project are not stored consistently:
 * bikes.createdAt is a serverTimestamp() (a Timestamp with .toDate()), while
 * users.createdAt is an ISO string written by app/license-upload.tsx. Older
 * records may have no createdAt at all. Normalise all of it here rather than
 * assuming a shape at each call site.
 */

export function ReasonBanner({ title, reason, when, tone: variant = 'danger' }) {
  const palette = variant === 'warning'
    ? { fg: T.warning, bg: T.warningDim, border: T.warningBorder }
    : { fg: T.danger, bg: T.dangerDim, border: T.dangerBorder };
  const stamp = when ? toDateSafe(when) : null;
  return (
    <div style={{
      display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 10,
      backgroundColor: palette.bg, border: `1px solid ${palette.border}`,
      borderRadius: 10, padding: 10,
    }}>
      <Icon name="alert" size={13} color={palette.fg} />
      <div style={{ fontSize: 12.5, color: palette.fg, lineHeight: 1.5 }}>
        <strong>{title}</strong>
        {reason ? ` ${reason}` : ' No reason was recorded.'}
        {stamp && (
          <span style={{ opacity: 0.75 }}>
            {' · '}{stamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>
    </div>
  );
}

/** Readable date for any of the shapes toDateSafe accepts. */

export function DateRangeFilter({ label, value, onChange }) {
  const { preset, from, to } = value;
  const active = preset !== 'all' || from || to;

  const setPreset = (key) => onChange({ preset: key, from: '', to: '' });
  const setBound = (which, v) => onChange({ ...value, preset: 'all', [which]: v });

  const inputStyle = {
    background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8,
    padding: '6px 9px', color: T.textPrimary, fontSize: 12.5,
    fontFamily: 'inherit', outline: 'none', colorScheme: 'dark light',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, whiteSpace: 'nowrap' }}>{label}</span>

      <div style={{
        display: 'inline-flex', flexWrap: 'wrap',
        backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
        borderRadius: 10, padding: 3, gap: 2,
      }}>
        {DATE_PRESETS.map(p => {
          const isOn = preset === p.key && !from && !to;
          return (
            <button key={p.key} onClick={() => setPreset(p.key)} className="ui-btn" style={{
              backgroundColor: isOn ? T.card : 'transparent',
              border: isOn ? `1px solid ${T.cardBorder}` : '1px solid transparent',
              boxShadow: isOn ? T.shadow : 'none',
              borderRadius: 8, padding: '6px 12px',
              color: isOn ? T.textPrimary : T.textMuted,
              cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
            }}>{p.label}</button>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <input type="date" value={from} max={to || undefined}
          onChange={e => setBound('from', e.target.value)} style={inputStyle} aria-label={`${label} from`} />
        <span style={{ color: T.textMuted, fontSize: 12 }}>to</span>
        <input type="date" value={to} min={from || undefined}
          onChange={e => setBound('to', e.target.value)} style={inputStyle} aria-label={`${label} to`} />
      </div>

      {active && (
        <button onClick={() => onChange({ preset: 'all', from: '', to: '' })} className="ui-btn" style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: T.primary, fontSize: 12.5, fontWeight: 700, padding: '4px 2px',
        }}>Clear</button>
      )}
    </div>
  );
}

export function SortToggle({ label, value, onChange }) {
  const options = [
    { key: 'desc', label: 'Newest first' },
    { key: 'asc', label: 'Oldest first' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{
        display: 'inline-flex', flexWrap: 'wrap',
        backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
        borderRadius: 10, padding: 3, gap: 2,
      }}>
        {options.map(o => {
          const active = value === o.key;
          return (
            <button key={o.key} onClick={() => onChange(o.key)} className="ui-btn" style={{
              backgroundColor: active ? T.card : 'transparent',
              border: active ? `1px solid ${T.cardBorder}` : '1px solid transparent',
              boxShadow: active ? T.shadow : 'none',
              borderRadius: 8, padding: '6px 12px',
              color: active ? T.textPrimary : T.textMuted,
              cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
            }}>
              <Icon name={o.key === 'desc' ? 'arrow-down' : 'arrow-up'} size={12}
                    color={active ? T.textPrimary : T.textMuted} />
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SearchBar({ value, onChange, placeholder = 'Search...' }) {
  return (
    <div className="ui-field" style={{
      display: 'flex', alignItems: 'center', gap: 9,
      backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
      borderRadius: 10, padding: '9px 12px', marginBottom: 16,
      maxWidth: 380,
    }}>
      <Icon name="search" size={15} color={T.textMuted} />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1, background: 'transparent', border: 'none', outline: 'none',
          color: T.textPrimary, fontSize: 13, fontFamily: 'inherit',
        }}
      />
      {value && (
        <button onClick={() => onChange('')} className="ui-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0 }}>
          <Icon name="x" size={14} color={T.textMuted} />
        </button>
      )}
    </div>
  );
}

// Generic confirm-before-destructive-action modal. If `requireReason` is set,
// Confirm stays disabled until the admin types something — used for
// reject/revoke actions so there's a record of *why*, and for force-cancel
// so a real production write always gets a deliberate second click.
// ── Replace identity document ────────────────────────────
// For users whose signup upload was unusable and who sent a proper document
// to support instead. The replacement becomes the document of record: it is
// what the Users list links to, what Set Photo crops from, and — since 1.3.0 —
// what the owner of a booking is shown (getBookingLicence reads
// users/{uid}/private/identity at view time).
//
// The old file is never deleted. Its URL is kept in `documentHistory` on the
// same private doc, so a dispute can still see what was originally submitted.
//
// Two slots, because an owner who added the renter side may hold a separate
// driving licence (`renterLicenseFile`, see app/add-role.tsx) beside their
// signup ID. The renter side reads that one first.

export function ConfirmModal({ open, title, message, requireReason, initialReason = '', confirmLabel = 'Confirm', danger, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  // `initialReason` lets a caller open this with the wording already filled
  // in — the automatic check offers one — while leaving it editable.
  useEffect(() => { if (open) setReason(initialReason || ''); }, [open, initialReason]);
  if (!open) return null;

  const canConfirm = !requireReason || reason.trim().length > 0;

  const tn = tone(danger ? 'danger' : 'primary');

  return (
    <div className="ui-overlay" style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(9, 12, 18, 0.55)',
      backdropFilter: 'blur(3px)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCancel}>
      <div
        className="ui-modal"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
          borderRadius: 14, padding: 24, width: '100%', maxWidth: 440,
          boxShadow: T.shadowLg,
        }}
      >
        <div style={{
          width: 38, height: 38, borderRadius: 10, marginBottom: 14,
          backgroundColor: tn.bg, border: `1px solid ${tn.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={danger ? 'alert' : 'check'} size={18} color={tn.color} />
        </div>
        <div style={{ color: T.textPrimary, fontSize: 16.5, fontWeight: 700, marginBottom: 7, letterSpacing: -0.3 }}>{title}</div>
        {message && <div style={{ color: T.textSecondary, fontSize: 13.5, lineHeight: 1.55, marginBottom: 18 }}>{message}</div>}
        {requireReason && (
          <textarea
            className="ui-field"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Reason (required)..."
            rows={3}
            style={{
              width: '100%', backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
              borderRadius: 10, padding: 11, color: T.textPrimary, fontSize: 13,
              fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: 18,
            }}
          />
        )}
        <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="ui-btn" style={{
            backgroundColor: 'transparent', border: `1px solid ${T.cardBorder}`,
            borderRadius: 9, padding: '8px 15px', color: T.textSecondary,
            cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
          }}>Cancel</button>
          <ActionBtn
            label={confirmLabel}
            icon={danger ? 'x' : 'check'}
            variant={danger ? 'reject' : 'approve'}
            disabled={!canConfirm}
            onClick={() => onConfirm(reason.trim())}
          />
        </div>
      </div>
    </div>
  );
}

// Renaming exists so the account name can be corrected to match the name on the
// verification document, so the document itself is put right next to the field
// — the whole job is comparing the two, and making the admin reopen the card to
// check would defeat the point.

export class PanelErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Panel crashed:', error, info);
  }

  componentDidUpdate(prev) {
    // Moving to another tab clears the error, so one bad panel does not
    // poison the rest of the session.
    if (prev.panelKey !== this.props.panelKey && this.state.error) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{
        backgroundColor: T.card, border: `1px solid ${T.dangerBorder}`, borderRadius: 14,
        padding: 24, color: T.textSecondary, fontSize: 13.5, lineHeight: 1.6,
      }}>
        <div style={{ color: T.textPrimary, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
          This panel could not be shown
        </div>
        <div style={{ marginBottom: 12 }}>
          Everything else still works — pick another tab from the sidebar. The details below
          help fix it.
        </div>
        <pre style={{
          backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`, borderRadius: 10,
          padding: 12, color: T.danger, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap',
        }}>{String(this.state.error?.message || this.state.error)}</pre>
        <ActionBtn label="Try again" icon="check" variant="approve" onClick={() => this.setState({ error: null })} />
      </div>
    );
  }
}

export function EmptyState({ message, icon = 'bike' }) {
  return (
    <div style={{
      textAlign: 'center', padding: '56px 20px',
      color: T.textMuted, fontSize: 14,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
      backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
      borderRadius: 14,
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} size={24} color={T.textMuted} />
      </div>
      {message}
    </div>
  );
}

// ── Login ────────────────────────────────────────────────
// ── User Profile Modal ────────────────────────────────────
// The one place that answers "who is this person" — reachable from any name in
// any tab. Read-only on purpose: approving, revoking and renaming stay in the
// Users tab so there is a single place those decisions are made.

export const PAGE_SIZE = 30;

export function usePaged(items, resetKey) {
  const [count, setCount] = useState(PAGE_SIZE);
  useEffect(() => { setCount(PAGE_SIZE); }, [resetKey]);
  return {
    visible: items.slice(0, count),
    remaining: Math.max(0, items.length - count),
    more: () => setCount(c => c + PAGE_SIZE),
  };
}

export function ShowMore({ remaining, onClick }) {
  if (!remaining) return null;
  return (
    <button onClick={onClick} className="ui-btn" style={{
      marginTop: 4, padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
      backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
      color: T.textSecondary, fontSize: 14, fontWeight: 700,
    }}>
      Show {Math.min(PAGE_SIZE, remaining)} more · {remaining} left
    </button>
  );
}

export function ProfileField({ label, children }) {
  return (
    <div>
      <div style={{ color: T.textMuted, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ color: T.textPrimary, fontSize: 13.5, wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

export function ProfileSection({ title, count, children }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{
        color: T.textMuted, fontSize: 12, fontWeight: 700,
        letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 10,
      }}>
        {title}{typeof count === 'number' ? ` — ${count}` : ''}
      </div>
      {children}
    </div>
  );
}

export function Lightbox({ photos, index, onIndex, onClose }) {
  const count = photos.length;
  const prev = useCallback(() => onIndex((index - 1 + count) % count), [index, count, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % count), [index, count, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, onClose]);

  const navBtn = {
    background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '50%', width: 42, height: 42, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.88)',
        zIndex: 400, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: 20, gap: 16,
      }}
    >
      <button
        onClick={onClose}
        style={{ ...navBtn, position: 'absolute', top: 20, right: 20 }}
      >
        <Icon name="x" size={20} color="#fff" />
      </button>

      <div
        onClick={e => e.stopPropagation()}
        style={{ display: 'flex', alignItems: 'center', gap: 16, maxWidth: '100%' }}
      >
        {count > 1 && (
          <button onClick={prev} style={navBtn}>
            <Icon name="chevronLeft" size={20} color="#fff" />
          </button>
        )}
        <img
          src={photos[index]}
          alt={PHOTO_ANGLES[index] || `Photo ${index + 1}`}
          style={{
            maxWidth: '78vw', maxHeight: '76vh', objectFit: 'contain',
            borderRadius: 10, display: 'block',
          }}
        />
        {count > 1 && (
          <button onClick={next} style={navBtn}>
            <Icon name="chevronRight" size={20} color="#fff" />
          </button>
        )}
      </div>

      <div onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
        <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
          {PHOTO_ANGLES[index] || `Photo ${index + 1}`}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12.5, marginTop: 3 }}>
          {index + 1} of {count}
          <a
            href={photos[index]} target="_blank" rel="noreferrer"
            style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 12, textDecoration: 'none', fontWeight: 600 }}
          >
            Open original
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Bikes ────────────────────────────────────────────────
// Module-level for the same reason as the user caches above: this panel is
// unmounted whenever the admin switches tabs.

export function PhotoGrid({ photos }) {
  if (!photos) return null;
  const entries = Object.entries(photos).filter(([, url]) => url);
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {entries.map(([angle, url]) => (
        <a key={angle} href={url} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
          <img src={url} alt={angle} style={{
            width: 84, height: 84, objectFit: 'cover', borderRadius: 10,
            border: `1px solid ${T.cardBorder}`,
          }} />
          <div style={{ color: T.textMuted, fontSize: 10, textAlign: 'center', marginTop: 3, textTransform: 'capitalize' }}>{angle}</div>
        </a>
      ))}
    </div>
  );
}

export function SummaryRow({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: `1px solid ${T.divider}` }}>
      <span style={{ color: T.textMuted, fontSize: 12 }}>{label}</span>
      <span style={{ color: T.textSecondary, fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

export const bikeThumbCache = new Map();

// Mirrors bikeThumbnailUri in app/constants/bikeModels.ts, so admin sees the
// same photo the renter does — including the cover the owner picked.

export const thumbFromBike = (b) => {
  const photos = Array.isArray(b?.photos) ? b.photos : [];
  if (photos.length === 0) return '';
  const i = b?.thumbnailIndex;
  if (typeof i === 'number' && i >= 0 && i < photos.length && photos[i]) return photos[i];
  return photos[2] || photos[0] || '';
};

// `idKey` is a comma-joined list rather than an array so the effect has a
// stable dependency without a memo at every call site.

export function useBikeThumbs(idKey) {
  const [, bump] = useState(0);

  useEffect(() => {
    const ids = idKey ? idKey.split(',').filter(Boolean) : [];
    const missing = ids.filter(id => !bikeThumbCache.has(id));
    if (missing.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      // `in` takes at most 30 values, so one read covers up to 30 bikes.
      for (let i = 0; i < missing.length; i += 30) {
        const chunk = missing.slice(i, i + 30);
        try {
          const snap = await getDocs(query(collection(db, 'bikes'), where(documentId(), 'in', chunk)));
          snap.docs.forEach(d => bikeThumbCache.set(d.id, thumbFromBike(d.data())));
        } catch (e) {
          console.error('Bike thumbnail load failed:', e);
        }
        // Anything the query did not return is a deleted bike. Cached as empty
        // so it is not re-requested on every render.
        chunk.forEach(id => { if (!bikeThumbCache.has(id)) bikeThumbCache.set(id, ''); });
      }
      if (!cancelled) bump(n => n + 1);
    })();

    return () => { cancelled = true; };
  }, [idKey]);

  return bikeThumbCache;
}

// Square-ish bike photo for a booking row, falling back to a neutral tile so
// rows keep a consistent shape whether or not a photo resolved.

export function BikeThumb({ src, size = 52, radius = 9 }) {
  if (src) {
    return (
      <img src={src} alt="" style={{
        width: size, height: size, objectFit: 'cover', borderRadius: radius,
        border: `1px solid ${T.cardBorder}`, flexShrink: 0, display: 'block',
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon name="bike" size={Math.round(size * 0.42)} color={T.textMuted} />
    </div>
  );
}

// ── Photo Crop Modal ──────────────────────────────────────
// Loads the user's uploaded document image, lets the admin drag/zoom
// to frame the face, then crops via canvas and uploads to Storage.
// Crops from a blob: object URL rather than the remote https URL. Loading the
// bytes ourselves keeps the canvas same-origin, so toBlob() can never throw a
// SecurityError from cross-origin tainting.
